import { parseClipboardHtmlTable } from '@/components/preview/test-table-editor/parseClipboardTable';
import { createId, createTableRow } from '@/lib/table-utils';
import type { ReportImportSectionId, ReportImportSegment } from '@/api/report-imports';
import type { ReportData } from '@/types/report';
import type { TableBlock, TestBlock } from '@/types/report-payload';

export type ReportImportSectionChoice = ReportImportSectionId | 'skip';

export type ReportImportProseSectionId = Exclude<ReportImportSectionId, 'tests'>;

export const REPORT_IMPORT_PROSE_SECTION_IDS: ReportImportProseSectionId[] = [
    'demand_description',
    'procedures',
    'behavior_observations',
    'clinical_history',
    'diagnosis',
    'recommendations',
];

export interface ReportImportPatch {
    /** Replacement content per prose section (never `tests`). */
    sections: Partial<
        Record<ReportImportProseSectionId, { content: string; section_title?: string }>
    >;
    /** Blocks to append to `tests.blocks` (text blocks + converted tables). */
    testsBlocks: TestBlock[];
    testsSectionTitle?: string;
}

const DEFAULT_COLUMN_WIDTH = 150;

/**
 * Default review-UI choice for a segment: anything the API could not place
 * (`unknown`) and title-only segments start as "skip" — the user re-maps
 * them explicitly instead of content being silently misfiled.
 */
export const defaultSectionChoiceFor = (segment: ReportImportSegment): ReportImportSectionChoice =>
    segment.suggested_section === 'unknown' || segment.empty_body
        ? 'skip'
        : segment.suggested_section;

const segmentDisplayTitle = (segment: ReportImportSegment): string | undefined =>
    segment.display_title ?? segment.title ?? undefined;

const tableElementToBlock = (table: Element, description: string): TableBlock | null => {
    const grid = parseClipboardHtmlTable(table.outerHTML);
    if (!grid?.length) return null;
    // Content-empty tables (decorative borders in Word) are not data.
    if (!grid.some((row) => row.some((cell) => cell.trim() !== ''))) return null;

    const columnCount = Math.max(...grid.map((row) => row.length));
    if (!columnCount) return null;

    const [headerValues, ...dataRows] = grid;

    return {
        id: createId('block'),
        block_type: 'table',
        order: 0,
        has_content: true,
        automatic: false,
        description,
        header: createTableRow(columnCount, headerValues),
        rows: dataRows.map((rowValues) => createTableRow(columnCount, rowValues)),
        columnWidths: Array.from({ length: columnCount }, () => DEFAULT_COLUMN_WIDTH),
    };
};

const hasVisibleContent = (html: string): boolean => {
    const container = document.createElement('div');
    container.innerHTML = html;
    return (container.textContent ?? '').trim().length > 0;
};

/**
 * Turns reviewed segments + the user's section choices into a report patch.
 * Text is carried over verbatim as HTML. Tables cannot live inside the prose
 * sections' rich-text model, so every `<table>` becomes a table block under
 * `tests`, labeled with its originating segment title.
 */
export const buildReportPatch = (
    segments: ReportImportSegment[],
    choices: Record<number, ReportImportSectionChoice>,
): ReportImportPatch => {
    const parser = new DOMParser();
    const sections: ReportImportPatch['sections'] = {};
    const testsBlocks: TestBlock[] = [];
    let testsSectionTitle: string | undefined;

    const appendTextBlock = (html: string) => {
        if (!hasVisibleContent(html)) return;
        testsBlocks.push({
            id: createId('block'),
            block_type: 'text',
            order: 0,
            has_content: true,
            automatic: false,
            content: html,
        });
    };

    for (const segment of segments) {
        const choice = choices[segment.index] ?? defaultSectionChoiceFor(segment);
        if (choice === 'skip') continue;

        const displayTitle = segmentDisplayTitle(segment);
        const body = parser.parseFromString(segment.html, 'text/html').body;
        const tableDescription = displayTitle ?? '';

        if (choice === 'tests') {
            if (displayTitle && !testsSectionTitle) testsSectionTitle = displayTitle;

            // Preserve the original order: contiguous prose becomes a text
            // block, each table becomes a table block.
            let textParts: string[] = [];
            for (const element of Array.from(body.children)) {
                if (element.tagName === 'TABLE') {
                    appendTextBlock(textParts.join(''));
                    textParts = [];
                    const block = tableElementToBlock(element, tableDescription);
                    if (block) testsBlocks.push(block);
                } else {
                    textParts.push(element.outerHTML);
                }
            }
            appendTextBlock(textParts.join(''));
            continue;
        }

        const textParts: string[] = [];
        for (const element of Array.from(body.children)) {
            if (element.tagName === 'TABLE') {
                const block = tableElementToBlock(element, tableDescription);
                if (block) testsBlocks.push(block);
            } else {
                textParts.push(element.outerHTML);
            }
        }

        const html = textParts.join('');
        if (!hasVisibleContent(html)) continue;

        const existing = sections[choice];
        sections[choice] = {
            content: existing ? `${existing.content}${html}` : html,
            section_title: existing?.section_title ?? displayTitle,
        };
    }

    return { sections, testsBlocks, testsSectionTitle };
};

/** True when at least one non-skipped segment carries content. */
export const reportImportPatchHasContent = (patch: ReportImportPatch): boolean =>
    Object.keys(patch.sections).length > 0 || patch.testsBlocks.length > 0;

/** True when the report already has prose or test-block content the import would replace. */
export const reportHasImportableSectionContent = (data: ReportData): boolean =>
    REPORT_IMPORT_PROSE_SECTION_IDS.some((sectionId) =>
        Boolean(data[sectionId]?.content?.trim()),
    ) || (data.tests?.blocks?.length ?? 0) > 0;

/**
 * Pure merge of an import patch into report data: mapped prose sections are
 * replaced (keeping their other fields), imported blocks are appended to the
 * tests section. The caller decides how to hydrate the editor with the result.
 */
export const applyReportImportPatch = (
    current: ReportData,
    patch: ReportImportPatch,
): ReportData => {
    const next: ReportData = { ...current };

    (
        Object.entries(patch.sections) as Array<
            [ReportImportProseSectionId, { content: string; section_title?: string }]
        >
    ).forEach(([sectionId, section]) => {
        next[sectionId] = {
            ...(current[sectionId] ?? {}),
            content: section.content,
            ...(section.section_title ? { section_title: section.section_title } : {}),
        };
    });

    if (patch.testsBlocks.length) {
        const currentTests = current.tests ?? { section_title: '', blocks: [] };
        const blocks = [...(currentTests.blocks ?? []), ...patch.testsBlocks].map(
            (block, index) => ({ ...block, order: index }),
        );
        next.tests = {
            ...currentTests,
            blocks,
            section_title: currentTests.section_title || patch.testsSectionTitle || '',
        };
    }

    return next;
};
